/**
 * FT-116 REWRITE: Migrate vessel-scoped Risk Assessments to fleet storage
 *
 * This migration consolidates all vessel-scoped RAs into the org-scoped
 * fleet_risk_assessments storage. Each migrated RA gets:
 * - assignedVessels: [originalVesselId] (starts with just the source vessel)
 * - homeVessel: originalVesselId (for tracking where it was created)
 *
 * The migration:
 * 1. Scans localStorage for all risk_assessments_* keys
 * 2. Copies records to fleet_risk_assessments with assignedVessels populated
 * 3. Backs up old keys to _backup_* (does NOT delete originals)
 * 4. Sets migration flag to prevent re-running
 *
 * @module storage/migrations/migrateVesselRAsToFleet
 */

import { devLog } from '../../utils/devLog';

const MIGRATION_FLAG = 'sr.migration.raToFleet_v1';
const FLEET_KEY = 'fleet_risk_assessments';

/**
 * Extract vessel ID from a storage key
 * Handles both patterns:
 * - risk_assessments_{vesselId}
 * - risk_assessments_{orgId}_{vesselId} (modern pattern)
 */
const extractVesselId = (key) => {
  // Pattern: risk_assessments_<uuid> or risk_assessments_<orgId>_<uuid>
  const parts = key.replace('risk_assessments_', '').split('_');
  // Last part is always the vessel ID
  return parts[parts.length - 1] || null;
};

/**
 * Check if a key is a vessel-scoped risk assessment key
 */
const isVesselRAKey = (key) => {
  return key.startsWith('risk_assessments_') &&
         !key.startsWith('risk_assessments_v2') && // Exclude legacy v2 keys
         key !== 'fleet_risk_assessments';
};

/**
 * Normalize a legacy RA record to fleet format
 */
const normalizeToFleet = (record, vesselId) => {
  const now = new Date().toISOString();

  return {
    // Preserve all original fields
    ...record,

    // Ensure required fields exist
    id: record.id,
    title: record.title || 'Untitled Assessment',
    status: record.status || 'active',

    // FT-116: Fleet-specific fields
    assignedVessels: [vesselId],  // Assign to source vessel only
    homeVessel: vesselId,         // Track where it was created

    // Migration metadata
    _migratedFrom: 'vessel_scoped',
    _migratedAt: now,
    _sourceKey: `risk_assessments_${vesselId}`
  };
};

/**
 * Run the RA to Fleet migration
 *
 * @param {Array<string>} vesselIds - Optional list of vessel IDs to migrate.
 *                                    If not provided, scans localStorage for all.
 * @returns {{ migrated: number, skipped: number, errors: string[] }}
 */
export function migrateVesselRAsToFleet(vesselIds = null) {
  // Check if already migrated
  const alreadyMigrated = localStorage.getItem(MIGRATION_FLAG);
  if (alreadyMigrated) {
    devLog('[FT-116 Migration] Already migrated on', alreadyMigrated);
    return { migrated: 0, skipped: 0, errors: [], alreadyDone: true };
  }

  devLog('[FT-116 Migration] Starting vessel RA to fleet migration...');

  const result = {
    migrated: 0,
    skipped: 0,
    errors: [],
    details: []
  };

  try {
    // Step 1: Get existing fleet RAs (to avoid duplicates)
    let existingFleetRAs = [];
    try {
      const raw = localStorage.getItem(FLEET_KEY);
      existingFleetRAs = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(existingFleetRAs)) {
        existingFleetRAs = [];
      }
    } catch (e) {
      devLog('[FT-116 Migration] Could not parse existing fleet RAs:', e);
      existingFleetRAs = [];
    }

    const existingIds = new Set(existingFleetRAs.map(ra => ra.id));
    devLog(`[FT-116 Migration] Found ${existingFleetRAs.length} existing fleet RAs`);

    // Step 2: Find all vessel-scoped RA keys
    const allKeys = Object.keys(localStorage);
    const vesselRAKeys = allKeys.filter(isVesselRAKey);
    devLog(`[FT-116 Migration] Found ${vesselRAKeys.length} vessel-scoped RA keys:`, vesselRAKeys);

    // Step 3: Process each vessel-scoped RA key
    for (const key of vesselRAKeys) {
      try {
        const vesselId = extractVesselId(key);
        if (!vesselId) {
          devLog(`[FT-116 Migration] Could not extract vessel ID from key: ${key}`);
          result.errors.push(`Invalid key format: ${key}`);
          continue;
        }

        // If vesselIds filter provided, skip non-matching
        if (vesselIds && !vesselIds.includes(vesselId)) {
          devLog(`[FT-116 Migration] Skipping ${key} - vessel not in filter list`);
          continue;
        }

        // Parse vessel RAs
        const rawData = localStorage.getItem(key);
        if (!rawData) {
          devLog(`[FT-116 Migration] Empty data for key: ${key}`);
          continue;
        }

        let vesselRAs;
        try {
          vesselRAs = JSON.parse(rawData);
          if (!Array.isArray(vesselRAs)) {
            vesselRAs = [];
          }
        } catch (e) {
          devLog(`[FT-116 Migration] Could not parse ${key}:`, e);
          result.errors.push(`Parse error for ${key}: ${e.message}`);
          continue;
        }

        devLog(`[FT-116 Migration] Processing ${vesselRAs.length} RAs from ${key}`);

        // Process each RA
        for (const ra of vesselRAs) {
          if (!ra || !ra.id) {
            result.skipped++;
            continue;
          }

          // Skip if already in fleet storage
          if (existingIds.has(ra.id)) {
            devLog(`[FT-116 Migration] Skipping duplicate: ${ra.id} (${ra.title})`);
            result.skipped++;
            continue;
          }

          // Normalize and add to fleet
          const fleetRA = normalizeToFleet(ra, vesselId);
          existingFleetRAs.push(fleetRA);
          existingIds.add(ra.id);
          result.migrated++;
          result.details.push({
            id: ra.id,
            title: ra.title,
            fromVessel: vesselId,
            status: ra.status
          });

          devLog(`[FT-116 Migration] Migrated: ${ra.title} (${ra.id}) from vessel ${vesselId}`);
        }

        // Step 4: Backup old key (DON'T delete - per Jonathan's condition)
        const backupKey = `_backup_${key}`;
        localStorage.setItem(backupKey, rawData);
        devLog(`[FT-116 Migration] Backed up ${key} to ${backupKey}`);

      } catch (e) {
        devLog(`[FT-116 Migration] Error processing ${key}:`, e);
        result.errors.push(`Error processing ${key}: ${e.message}`);
      }
    }

    // Step 5: Save updated fleet RAs
    if (result.migrated > 0) {
      localStorage.setItem(FLEET_KEY, JSON.stringify(existingFleetRAs));
      devLog(`[FT-116 Migration] Saved ${existingFleetRAs.length} total fleet RAs`);
    }

    // Step 6: Set migration flag
    const timestamp = new Date().toISOString();
    localStorage.setItem(MIGRATION_FLAG, timestamp);
    devLog(`[FT-116 Migration] Complete! Migrated: ${result.migrated}, Skipped: ${result.skipped}, Errors: ${result.errors.length}`);

    // Log summary to console for debugging
    if (process.env.NODE_ENV !== 'production') {
      console.log('[FT-116 Migration] Summary:', {
        migrated: result.migrated,
        skipped: result.skipped,
        errors: result.errors,
        details: result.details
      });
    }

    return result;

  } catch (e) {
    devLog('[FT-116 Migration] Fatal error:', e);
    result.errors.push(`Fatal error: ${e.message}`);
    return result;
  }
}

/**
 * Check if migration has been run
 */
export function isMigrationComplete() {
  return !!localStorage.getItem(MIGRATION_FLAG);
}

/**
 * Get migration timestamp
 */
export function getMigrationTimestamp() {
  return localStorage.getItem(MIGRATION_FLAG);
}

/**
 * Force re-run migration (for testing/debugging)
 * Clears the migration flag so it will run again
 */
export function resetMigrationFlag() {
  localStorage.removeItem(MIGRATION_FLAG);
  devLog('[FT-116 Migration] Migration flag reset - will re-run on next init');
}

/**
 * Rollback migration (restore from backups)
 * WARNING: This is for emergency use only
 */
export function rollbackMigration() {
  devLog('[FT-116 Migration] Rolling back...');

  const allKeys = Object.keys(localStorage);
  const backupKeys = allKeys.filter(k => k.startsWith('_backup_risk_assessments_'));

  for (const backupKey of backupKeys) {
    const originalKey = backupKey.replace('_backup_', '');
    const data = localStorage.getItem(backupKey);
    if (data) {
      localStorage.setItem(originalKey, data);
      devLog(`[FT-116 Migration] Restored ${originalKey} from backup`);
    }
  }

  // Clear migration flag
  localStorage.removeItem(MIGRATION_FLAG);
  devLog('[FT-116 Migration] Rollback complete');
}

export default migrateVesselRAsToFleet;
